import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import Select from '../common/Select';
import Badge from '../common/Badge';
import ProductCard, { ConstructionProduct } from './ProductCard';
import InvoiceEmailModal from './InvoiceEmailModal';
import { api } from '../../api/client';
import {
  generateInvoicePDF,
  downloadInvoicePDF,
  createPDFPreviewURL,
  calculateInvoiceTotals,
  InvoiceData,
  InvoiceProduct,
} from '../../utils/invoice-pdf';

interface InvoiceEditorProps {
  constructionId: string;
  constructionTitle: string;
  constructionNumber?: string;
  companyName?: string;
  companyAddress?: string;
  companyPostalCode?: string;
  companyEmail?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  invoiceAmount?: number;
  invoiceStatus?: string;
  paymentDate?: string;
  products?: ConstructionProduct[];
  onSave: (data: InvoiceSaveData) => Promise<void>;
  onEmailSent?: () => void;
  readOnly?: boolean;
}

export interface InvoiceSaveData {
  invoiceNumber: string;
  invoiceDate: string;
  invoiceAmount: number;
  invoiceStatus: string;
  paymentDate?: string;
  products: ConstructionProduct[];
  notes?: string;
}

const invoiceStatusOptions = [
  { value: '', label: '未設定' },
  { value: 'draft', label: '下書き' },
  { value: 'pending', label: '未請求' },
  { value: 'issued', label: '請求済(送付)' },
  { value: 'sent', label: '請求済' },
  { value: 'paid', label: '入金済' },
  { value: 'overdue', label: '延滞' },
];

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY' }).format(amount);
};

export default function InvoiceEditor({
  constructionId,
  constructionTitle,
  constructionNumber,
  companyName = '',
  companyAddress = '',
  companyPostalCode = '',
  companyEmail = '',
  invoiceNumber: initialInvoiceNumber = '',
  invoiceDate: initialInvoiceDate = '',
  invoiceAmount: initialInvoiceAmount = 0,
  invoiceStatus: initialInvoiceStatus = '',
  paymentDate: initialPaymentDate = '',
  products: initialProducts = [],
  onSave,
  onEmailSent,
  readOnly = false,
}: InvoiceEditorProps) {
  // 日付をyyyy-MM-dd形式に変換するヘルパー関数
  const formatDateForInput = (dateStr: string | undefined): string => {
    if (!dateStr) return '';
    // ISO 8601形式（2026-01-27T00:00:00.000Z）をyyyy-MM-dd形式に変換
    if (dateStr.includes('T')) {
      return dateStr.split('T')[0];
    }
    return dateStr;
  };

  const [invoiceNumber, setInvoiceNumber] = useState(initialInvoiceNumber);
  const [invoiceDate, setInvoiceDate] = useState(formatDateForInput(initialInvoiceDate) || new Date().toISOString().split('T')[0]);
  const [invoiceStatus, setInvoiceStatus] = useState(initialInvoiceStatus);
  const [paymentDate, setPaymentDate] = useState(formatDateForInput(initialPaymentDate));
  const [products, setProducts] = useState<ConstructionProduct[]>(initialProducts);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);

  // propsの変更を監視してstateを同期
  useEffect(() => {
    if (initialInvoiceDate) {
      setInvoiceDate(formatDateForInput(initialInvoiceDate));
    }
  }, [initialInvoiceDate]);

  useEffect(() => {
    if (initialInvoiceNumber) {
      setInvoiceNumber(initialInvoiceNumber);
    }
  }, [initialInvoiceNumber]);

  useEffect(() => {
    setInvoiceStatus(initialInvoiceStatus);
  }, [initialInvoiceStatus]);

  useEffect(() => {
    setPaymentDate(formatDateForInput(initialPaymentDate));
  }, [initialPaymentDate]);

  useEffect(() => {
    setProducts(initialProducts);
  }, [initialProducts]);

  // 請求金額の計算
  const { subtotal, taxAmount, totalAmount } = calculateInvoiceTotals(
    products.map(p => ({
      productName: p.productName,
      model: p.productModel,
      quantity: p.quantity,
      unitPrice: p.unitPrice,
      amount: p.quantity * p.unitPrice,
    }))
  );

  // 請求書番号を自動生成
  useEffect(() => {
    if (!invoiceNumber && constructionNumber) {
      const today = new Date();
      const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}${String(today.getDate()).padStart(2, '0')}`;
      setInvoiceNumber(`INV-${dateStr}-${constructionNumber.replace('CON-', '')}`);
    }
  }, [invoiceNumber, constructionNumber]);

  // 製品追加
  const handleAddProduct = () => {
    const newProduct: ConstructionProduct = {
      id: crypto.randomUUID(),
      productName: '',
      productModel: '',
      quantity: 1,
      unitPrice: 0,
    };
    setProducts([...products, newProduct]);
  };

  // 製品更新
  const handleUpdateProduct = (id: string, field: keyof ConstructionProduct, value: string | number) => {
    setProducts(products.map(p =>
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  // 製品削除
  const handleDeleteProduct = (id: string) => {
    setProducts(products.filter(p => p.id !== id));
  };

  // 請求書番号の重複チェック
  const checkDuplicateInvoiceNumber = async (num: string): Promise<boolean> => {
    try {
      const filter = JSON.stringify([{ field: 'invoiceNumber', operator: 'eq', value: num }]);
      const data = await api.get<{ items: Array<{ id: string }> }>(
        `/api/constructions?advancedSearch=${encodeURIComponent(filter)}&pageSize=1`
      );
      const items = data?.items || [];
      return items.some(item => item.id !== constructionId);
    } catch {
      return false;
    }
  };

  // 保存
  const handleSave = async () => {
    if (saving) return;

    if (!invoiceNumber.trim()) {
      toast.error('請求書番号を入力してください');
      return;
    }
    if (!invoiceDate) {
      toast.error('請求日を入力してください');
      return;
    }
    if (!invoiceStatus) {
      toast.error('請求ステータスを選択してください');
      return;
    }
    if (products.length === 0) {
      toast.error('製品明細を1件以上追加してください');
      return;
    }
    if (products.some(p => p.unitPrice < 0 || p.quantity < 0)) {
      toast.error('単価・数量に負の値は設定できません');
      return;
    }

    setSaving(true);
    try {
      const isDuplicate = await checkDuplicateInvoiceNumber(invoiceNumber.trim());
      if (isDuplicate) {
        toast.error(`請求書番号「${invoiceNumber}」は既に使用されています。別の番号を入力してください。`);
        return;
      }

      await onSave({
        invoiceNumber,
        invoiceDate,
        invoiceAmount: totalAmount,
        invoiceStatus,
        paymentDate: paymentDate || undefined,
        products,
        notes: notes || undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  // PDF生成用のInvoiceDataを作成
  const createInvoiceData = (): InvoiceData => {
    const invoiceProducts: InvoiceProduct[] = products.map(p => ({
      id: p.id,
      productName: p.productName,
      model: p.productModel,
      quantity: p.quantity,
      unitPrice: p.unitPrice,
      amount: p.quantity * p.unitPrice,
    }));

    return {
      invoiceNumber,
      invoiceDate,
      companyName,
      companyAddress,
      companyPostalCode,
      constructionTitle,
      constructionNumber,
      products: invoiceProducts,
      subtotal,
      taxRate: 10,
      taxAmount,
      totalAmount,
      notes,
    };
  };

  // PDFプレビュー
  const handlePreview = async () => {
    setGenerating(true);
    try {
      const invoiceData = createInvoiceData();
      const { blob, base64 } = await generateInvoicePDF(invoiceData);
      const url = createPDFPreviewURL(blob);
      setPreviewUrl(url);
      setPdfBase64(base64);
    } catch (error) {
      console.error('PDF生成エラー:', error);
      toast.error('PDFの生成に失敗しました');
    } finally {
      setGenerating(false);
    }
  };

  // PDFダウンロード
  const handleDownload = async () => {
    setGenerating(true);
    try {
      const invoiceData = createInvoiceData();
      const { blob } = await generateInvoicePDF(invoiceData);
      downloadInvoicePDF(blob, `請求書_${invoiceNumber}.pdf`);
    } catch (error) {
      console.error('PDF生成エラー:', error);
      toast.error('PDFの生成に失敗しました');
    } finally {
      setGenerating(false);
    }
  };

  // メール送信モーダルを開く
  const handleOpenEmailModal = async () => {
    let currentBase64 = pdfBase64;
    if (!currentBase64) {
      setGenerating(true);
      try {
        const invoiceData = createInvoiceData();
        const { base64 } = await generateInvoicePDF(invoiceData);
        setPdfBase64(base64);
        currentBase64 = base64;
      } catch (error) {
        console.error('PDF生成エラー:', error);
        toast.error('PDFの生成に失敗しました');
        setGenerating(false);
        return;
      }
      setGenerating(false);
    }
    const pdfSizeBytes = currentBase64.length * 0.75;
    if (pdfSizeBytes > 25 * 1024 * 1024) {
      toast.error('PDFファイルが25MBを超えているため、メールで送信できません。');
      return;
    }
    setShowEmailModal(true);
  };

  // プレビューURLのクリーンアップ
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; variant: 'default' | 'info' | 'success' | 'warning' | 'danger' }> = {
      draft: { label: '下書き', variant: 'default' },
      pending: { label: '未請求', variant: 'warning' },
      sent: { label: '請求済', variant: 'info' },
      paid: { label: '入金済', variant: 'success' },
      overdue: { label: '延滞', variant: 'danger' },
    };
    const info = statusMap[status] || { label: '未設定', variant: 'default' as const };
    return <Badge variant={info.variant}>{info.label}</Badge>;
  };

  return (
    <Card title="請求書">
      {/* ステータスバッジ */}
      {invoiceStatus && (
        <div className="mb-4 -mt-2">
          {getStatusBadge(invoiceStatus)}
        </div>
      )}
      <div className="space-y-6">
        {/* 請求書基本情報 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="請求書番号"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            placeholder="INV-YYYYMMDD-0001"
            disabled={readOnly}
          />
          <Input
            label="請求日"
            type="date"
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
            disabled={readOnly}
          />
          <Select
            label="請求ステータス"
            value={invoiceStatus}
            onChange={(e) => setInvoiceStatus(e.target.value)}
            disabled={readOnly}
          >
            {invoiceStatusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="入金日"
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            disabled={readOnly}
          />
        </div>

        {/* 宛先情報 */}
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">宛先</h4>
          <p className="text-gray-900 dark:text-white font-medium">{companyName || '(企業未設定)'} 御中</p>
          {companyPostalCode && <p className="text-sm text-gray-600 dark:text-gray-400">{companyPostalCode}</p>}
          {companyAddress && <p className="text-sm text-gray-600 dark:text-gray-400">{companyAddress}</p>}
        </div>

        {/* 製品明細 */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">製品明細</h4>
            {!readOnly && (
              <Button variant="secondary" size="sm" onClick={handleAddProduct}>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                製品追加
              </Button>
            )}
          </div>

          {products.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <p>製品が登録されていません</p>
              {!readOnly && (
                <Button variant="secondary" size="sm" className="mt-3" onClick={handleAddProduct}>
                  製品を追加
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onUpdate={handleUpdateProduct}
                  onDelete={handleDeleteProduct}
                  readOnly={readOnly}
                />
              ))}
            </div>
          )}
        </div>

        {/* 合計金額 */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">小計</span>
              <span className="text-gray-900 dark:text-white">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">消費税 (10%)</span>
              <span className="text-gray-900 dark:text-white">{formatCurrency(taxAmount)}</span>
            </div>
            <div className="border-t border-blue-200 dark:border-blue-700 pt-2 flex justify-between">
              <span className="font-medium text-gray-900 dark:text-white">合計金額</span>
              <span className="text-xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(totalAmount)}</span>
            </div>
          </div>
        </div>

        {/* 備考 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">備考</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="振込先情報やお支払い期限などを記載..."
            disabled={readOnly}
          />
        </div>

        {/* PDFプレビュー */}
        {previewUrl && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="bg-gray-100 dark:bg-gray-800 px-4 py-2 flex justify-between items-center">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">PDFプレビュー</span>
              <button
                onClick={() => setPreviewUrl(null)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <iframe
              src={previewUrl}
              className="w-full max-h-[50vh] min-h-[300px]"
              title="請求書プレビュー"
            />
          </div>
        )}

        {/* アクションボタン */}
        <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          {!readOnly && (
            <Button onClick={handleSave} loading={saving}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              保存
            </Button>
          )}
          
          <Button variant="secondary" onClick={handlePreview} loading={generating}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            プレビュー
          </Button>
          
          <Button variant="secondary" onClick={handleDownload} loading={generating}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            ダウンロード
          </Button>
          
          <Button variant="primary" onClick={handleOpenEmailModal} loading={generating}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            メール送信
          </Button>
        </div>
      </div>

      {/* メール送信モーダル */}
      {showEmailModal && pdfBase64 && (
        <InvoiceEmailModal
          constructionId={constructionId}
          invoiceNumber={invoiceNumber}
          companyName={companyName}
          companyEmail={companyEmail}
          totalAmount={totalAmount}
          pdfBase64={pdfBase64}
          onClose={() => setShowEmailModal(false)}
          onSuccess={() => {
            setShowEmailModal(false);
            setInvoiceStatus('sent');
            onEmailSent?.();
          }}
        />
      )}
    </Card>
  );
}
